/**
 * Hooks for the export pipeline.
 *
 * Design reference: ``backend/docs/batch_19_design.md`` §3, §9.5.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";

interface ExportRow {
  id: string;
  kind: string;
  scope_id: string | null;
  date_range_start: string | null;
  date_range_end: string | null;
  requested_by: string;
  status: string;
  size_bytes: number | null;
  error_code: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string;
}

interface CreateExportBody {
  kind: string;
  scope_id?: string;
  date_range_start?: string;
  date_range_end?: string;
}

/** List recent exports for the tenant. */
export function useExports() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<ExportRow[]>({
    queryKey: ["exports", tenantSlug],
    queryFn: async () => {
      const r = await apiClient.get<ExportRow[]>(`/api/t/${tenantSlug}/exports`);
      return r.data;
    },
    enabled: !!tenantSlug,
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!rows) return false;
      const hasPending = rows.some((r) => r.status === "pending" || r.status === "running");
      return hasPending ? 5000 : false;
    },
    // Keep polling even when the tab/window is not focused. Without this, React
    // Query's interval tick is a no-op while `focusManager.isFocused()` is false
    // (queryObserver gates each tick on `refetchIntervalInBackground || isFocused`).
    // An export is a fire-and-forget async job — the user may click out, switch
    // tabs, or be in a context the browser doesn't report as focused — so the
    // status poll must advance regardless of focus. (D10 S1: the poll fired once
    // on mount then never re-fetched because every interval tick saw isFocused()
    // === false; this matches `useCandidatePolling`'s background-poll pattern.)
    refetchIntervalInBackground: true,
  });
}

/** Single export status (polled while pending/running). */
export function useExport(exportId: string | null) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<ExportRow>({
    queryKey: ["exports", tenantSlug, exportId],
    queryFn: async () => {
      const r = await apiClient.get<ExportRow>(
        `/api/t/${tenantSlug}/exports/${exportId}`
      );
      return r.data;
    },
    enabled: !!tenantSlug && !!exportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "running" ? 5000 : false;
    },
    // See `useExports` above — poll the single export's status independent of
    // window focus so the "Generating…" → "complete" transition is observed even
    // when the tab isn't focused. (D10 S1.)
    refetchIntervalInBackground: true,
  });
}

/** Create a new export (POST /exports). */
export function useCreateExport() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();

  return useMutation<{ id: string; status: string }, Error, CreateExportBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<{ id: string; status: string }>(
        `/api/t/${tenantSlug}/exports`,
        body
      );
      return r.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["exports", tenantSlug] });
    },
  });
}
